import React, { useState } from "react";
import AnnAvatar from "../../../../../images/AnnAvatar.svg";
import UserAvatar from "../../../../../images/UserAvatar.svg";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import {
  MessageContainer,
  MessageWrapper,
  ChatWindowAvatar,
  MessageButtonsWrapper,
  EditMessageWrapper,
  EditMessageText,
  EditMessageInput,
  EditMessageInputWrapper,
} from "./ChatBox.styled";
import { Icon, RoundedButton } from "../../../../../elements";

const ShadowContent = ({ htmlContent }) => {
  const hostRef = React.useRef(null);

  React.useEffect(() => {
    if (hostRef.current) {
      let shadow = hostRef.current.shadowRoot;
      if (!shadow) {
        shadow = hostRef.current.attachShadow({ mode: 'open' });
        const styleEl = document.createElement('style');
        styleEl.textContent = `
          :host {
            display: block;
            font-family: Arial, sans-serif; /* Fallback font */
            font-size: 14px; /* Fallback size */
            color: #333; /* Fallback text color */
            line-height: 1.5;
            width: 100%; /* From .html-content */
            overflow-x: auto; /* From .html-content */
          }
          /* Styles previously in MessageWrapper for .html-content */
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 8px 0;
          }
          th, td {
            border: 1px solid #E0E0E0; /* Approximate theme.colors.lightGrey */
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #F5F5F5; /* Approximate theme.colors.culturedGray */
          }
          pre {
            background-color: #F5F5F5; /* Approximate theme.colors.culturedGray */
            padding: 1em;
            border-radius: 6px;
            overflow-x: auto;
            white-space: pre-wrap; /* Allow wrapping for long lines */
            word-wrap: break-word; /* Break words if necessary */
          }
          code {
            background-color: #F5F5F5; /* Approximate theme.colors.culturedGray */
            padding: 0.2em 0.4em;
            border-radius: 3px;
            font-family: monospace;
            word-wrap: break-word; /* Break words if necessary */
          }
          p {
            margin: 0.5em 0;
          }
          ul, ol {
            margin: 1em 0;
            padding-left: 2em;
          }
          li {
            margin: 0.5em 0;
          }
          /* Additional useful base styles */
          a { color: #0066cc; text-decoration: underline; }
          img { max-width: 100%; height: auto; }
        `;
        shadow.appendChild(styleEl);
        
        const contentSlot = document.createElement('div');
        contentSlot.className = 'content-slot';
        shadow.appendChild(contentSlot);
      }
      const slot = shadow.querySelector('.content-slot');
      if (slot) {
        slot.innerHTML = htmlContent;
      }
    }
  }, [htmlContent]);

  return <div ref={hostRef} className="html-content"></div>;
};

// isHTML function (if not already defined/imported from a shared util)
const isHTMLHelper = (str) => { // Renamed to avoid conflict if Message component re-declares it
  if (!str || typeof str !== 'string') return false;
  const doc = new DOMParser().parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

const Message = ({ messageItem, key, displayButtons, width, agentAvatar }) => {
  const [setDisplayMessageButtons] =
    useState(displayButtons);
  const [displayMessageView, setDisplayMessageView] = useState(null);

  // createMarkup is no longer used directly for ShadowDOM, but sanitize config is useful
  const commonHtmlSanitizeConfig = {
    ALLOWED_TAGS: [
      'html', 'head', 'body', 'style', 'title', 'meta',
      'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'img',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
      'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'del'
    ],
    ALLOWED_ATTR: ['class', 'id', 'style', 'href', 'src', 'alt', 'title', 'target', 'rel', 'charset', 'name', 'content', 'type'],
    ALLOWED_CSS_PROPERTIES: [
        // Basic text styling
        'color', 'background-color', 
        'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
        'text-align', 'text-decoration', 'text-transform',
        // Box model & layout
        'width', 'height',
        'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
        'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
        'border', 'border-width', 'border-style', 'border-color', 'border-radius',
        'border-collapse', 'border-spacing',
        'display', 'float', 'clear', 'position', 'top', 'right', 'bottom', 'left', 'z-index',
        'overflow', 'overflow-x', 'overflow-y',
        // List styling
        'list-style', 'list-style-type',
        // Table styling
        'caption-side',
        // Other potentially useful
        'vertical-align', 'white-space', 'word-wrap', 'word-break', 'opacity', 'max-width'
      ]
  };

  // isHTML function defined within component or use isHTMLHelper
  const isHTML = (str) => isHTMLHelper(str);

  const renderContent = (message) => {
    // Type validation to prevent rendering objects
    if (typeof message !== 'string') {
      console.error('Message is not a string:', message);
      // Try to extract string from object
      if (typeof message === 'object' && message !== null) {
        if (message.message) return renderContent(message.message);
        if (message.content) return renderContent(message.content);
        if (message.text) return renderContent(message.text);
        return (
          <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
            Error rendering message: Invalid message format
          </div>
        );
      }
      return <div style={{ color: 'red' }}>Unable to display message</div>;
    }

    // 1. Explicit ```html block
    const htmlBlockRegex = /```html\s*([\s\S]*?)\s*```/;
    const htmlMatch = message.match(htmlBlockRegex);
    if (htmlMatch && htmlMatch[1]) {
      const extractedHtml = htmlMatch[1].trim();
      return <ShadowContent htmlContent={DOMPurify.sanitize(extractedHtml, commonHtmlSanitizeConfig)} />;
    }

    // 2. Generic ``` block, check if its content is HTML
    const genericCodeBlockRegex = /```\s*([\s\S]*?)\s*```/;
    const genericMatch = message.match(genericCodeBlockRegex);
    if (genericMatch && genericMatch[1]) {
      const potentialHtmlFromGenericBlock = genericMatch[1].trim();
      if (isHTML(potentialHtmlFromGenericBlock)) {
        return <ShadowContent htmlContent={DOMPurify.sanitize(potentialHtmlFromGenericBlock, commonHtmlSanitizeConfig)} />;
      }
    }

    // 3. Entire message is HTML
    if (isHTML(message)) {
      return <ShadowContent htmlContent={DOMPurify.sanitize(message, commonHtmlSanitizeConfig)} />;
    }

    // 4. Fallback: render original message with ReactMarkdown
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {message}
      </ReactMarkdown>
    );
  };

  return (
    <MessageContainer key={key} itemType={messageItem.type} width={width}>
      {messageItem.type === "sent" ? (
        <ChatWindowAvatar src={UserAvatar} alt="Agent Avatar" />
      ) : (
        <ChatWindowAvatar src={agentAvatar || AnnAvatar} alt="Agent Avatar" />
      )}

      {!displayMessageView && (
        <MessageWrapper itemType={messageItem.type}>
          {renderContent(messageItem.message)}
        </MessageWrapper>
      )}

      {messageItem.type !== "sent" && displayMessageView === "initial" && (
        <EditMessageWrapper>
          <Icon name={"head1"} height={"120px"} width={"120px"} />
          <EditMessageText>Help me to improve!</EditMessageText>
          <RoundedButton
            width="20%"
            onClick={() => setDisplayMessageView("edit")}
          >
            Train me
          </RoundedButton>
        </EditMessageWrapper>
      )}

      {messageItem.type !== "sent" && displayMessageView === "edit" && (
        <EditMessageInputWrapper>
          <EditMessageInput
            placeholder="Feel free to enter the correct answer right here."
            rows="10"
            cols="50"
          ></EditMessageInput>

          <MessageButtonsWrapper mt="5px">
            <Icon name={"thumbsDown"} height={"15px"} width={"15px"} />
            <RoundedButton
              width="150px"
              height="40px"
              fontSize="15px"
              onClick={() => {
                setDisplayMessageView(null);
                setDisplayMessageButtons(false);
              }}
            >
              Train
            </RoundedButton>
          </MessageButtonsWrapper>
        </EditMessageInputWrapper>
      )}
    </MessageContainer>
  );
};

export default Message;
